import { Fragment } from "react";
import { renderLatex } from "@/lib/katex-config";

/**
 * Inline maths in Ask Lilia's text: `$y=\sqrt{x}$` is drawn with KaTeX
 * instead of shown as typed. Pandoc's rule decides what is maths, so prices
 * stay text: the opening `$` is followed by a non-space, the closing `$`
 * follows a non-space and is not followed by a digit ("$5 and $10" is text).
 * Model text is untrusted, so KaTeX runs without `trust` (no \href, \html…).
 */
const INLINE_MATH = /\$(?=\S)([^$\n]*?\S)\$(?!\d)/g;

export function splitInlineMath(text: string): Array<{ math: boolean; text: string }> {
  const parts: Array<{ math: boolean; text: string }> = [];
  let last = 0;
  for (const m of text.matchAll(INLINE_MATH)) {
    const at = m.index ?? 0;
    // An escaped \$ is a dollar sign, not maths.
    if (at > 0 && text[at - 1] === "\\") continue;
    if (at > last) parts.push({ math: false, text: text.slice(last, at) });
    parts.push({ math: true, text: m[1] });
    last = at + m[0].length;
  }
  if (last < text.length) parts.push({ math: false, text: text.slice(last) });
  return parts;
}

function renderInline(latex: string): string | null {
  try {
    return renderLatex(latex, { displayMode: false, throwOnError: true, trust: false });
  } catch {
    return null;
  }
}

export function MathText({ text }: { text: string }) {
  return (
    <>
      {splitInlineMath(text).map((p, i) => {
        if (!p.math) return <Fragment key={i}>{p.text}</Fragment>;
        const html = renderInline(p.text);
        // Maths KaTeX can't read stays as the author would have typed it.
        return html === null ? (
          <Fragment key={i}>${p.text}$</Fragment>
        ) : (
          <span key={i} data-math="inline" dangerouslySetInnerHTML={{ __html: html }} />
        );
      })}
    </>
  );
}

/** Marks a code span that holds maths protected from Markdown (below). */
export const MATH_MARK = "\u2063";

/**
 * Markdown would eat maths: `\{` loses its backslash, `x_1 y_2` turns into
 * emphasis. So before Markdown runs, each maths span outside code becomes an
 * inline code span starting with MATH_MARK; the code renderer draws it with
 * {@link MathText}. Fenced blocks and existing code spans are left alone.
 */
export function protectInlineMath(markdown: string): string {
  return markdown
    .split(/(```[\s\S]*?(?:```|$)|`[^`\n]*`)/)
    .map((part, i) => {
      if (i % 2 === 1) return part;
      return part.replace(INLINE_MATH, (whole, _inner: string, at: number, all: string) =>
        (at > 0 && all[at - 1] === "\\") || whole.includes("`") ? whole : "`" + MATH_MARK + whole + "`",
      );
    })
    .join("");
}

/** The maths of a protected code span, or null for ordinary code. */
export function protectedMath(code: string): string | null {
  return code.startsWith(MATH_MARK) ? code.slice(MATH_MARK.length) : null;
}
